import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import useGentian from '../../core/hooks/useGentian';
import { ICredit } from '../../core/interfaces/credit.interface';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import IconButton from '@mui/material/IconButton';

const AdminPanel = () => {
  const { getResellersWithCredit, getResellers, createOrUpdateCredit, useAccount, runMonthlyInvoices } =
    useGentian();
  const { data: creditRows = [], isLoading: creditLoading, error, refetch } = getResellersWithCredit;
  const { data: resellers = [], isLoading: resellersLoading } = getResellers;

  const navigate = useNavigate();

  const { mutate, isLoading: saving } = createOrUpdateCredit;
  const { mutate: triggerRunMonthlyInvoices, isLoading: isRunningMonthlyInvoices } = runMonthlyInvoices;

  // local state
  const [editedLimits, setEditedLimits] = useState<Record<string, number>>({});
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});
  const [savedRows, setSavedRows] = useState<Record<string, boolean>>({});
  const [invoiceRunStatus, setInvoiceRunStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [invoiceRunMessage, setInvoiceRunMessage] = useState('');
  const { data: accountData } = useAccount();
  const rows = useMemo(() => {
    const creditByUserId = new Map<string, any>();
    creditRows.forEach((row: any) => {
      if (row?.userId) {
        creditByUserId.set(row.userId, row);
      }
    });

    const resellerOnly = resellers.filter((reseller: any) => reseller.user_metadata?.reseller === true);

    const merged = resellerOnly.map((reseller: any) => {
      const credit = creditByUserId.get(reseller.user_id);
      const name = reseller.name || reseller.user_metadata?.name || reseller.user_id;
      const email = reseller.email || reseller.user_metadata?.email || '';

      return {
        ...credit,
        userId: reseller.user_id,
        name,
        email,
        debtLimit: credit?.debtLimit ?? 0,
        amountUsed: credit?.amountUsed ?? 0,
        totalSpend: credit?.totalSpend ?? 0,
      };
    });

    return merged;
  }, [creditRows, resellers]);

  if (creditLoading || resellersLoading) return <Container>Loading…</Container>;
  if (error) return <Container>Failed to load data</Container>;
  const handleSave = (row: any) => {
    const updatedBy =
      typeof accountData?.data?.name === 'string'
        ? accountData.data.name
        : typeof accountData?.data?.user_metadata?.name === 'string'
          ? accountData.data.user_metadata.name
          : (accountData?.data?.user_id as string);

    const payload: ICredit & { customerName: string; email: string } = {
      _id: row._id,
      userId: row.userId,
      customerName: row.name,
      email: row.email,
      debtLimit: editedLimits[row.userId] ?? row.debtLimit ?? 0,
      amountUsed: row.amountUsed ?? 0,
      totalSpend: row.totalSpend ?? 0,
      lastPaidAt: row.lastPaidAt ?? new Date(),
      createdAt: row.createdAt ?? new Date(),
      updatedAt: new Date(),
      updatedBy,
    };

    mutate(payload, {
      onSuccess: () => {
        refetch();

        // exit edit mode
        setEditingRows((prev) => ({ ...prev, [row.userId]: false }));

        // clear local edits
        setEditedLimits((prev) => {
          const copy = { ...prev };
          delete copy[row.userId];
          return copy;
        });

        // show saved banner
        setSavedRows((prev) => ({ ...prev, [row.userId]: true }));

        setTimeout(() => {
          setSavedRows((prev) => ({ ...prev, [row.userId]: false }));
        }, 3000);
      },
    });
  };

  const handleCancel = (userId: string) => {
    // revert local edit
    setEditedLimits((prev) => {
      const copy = { ...prev };
      delete copy[userId];
      return copy;
    });

    // exit edit mode
    setEditingRows((prev) => ({ ...prev, [userId]: false }));
  };

  const handleRunMonthlyInvoices = () => {
    setInvoiceRunStatus('idle');
    setInvoiceRunMessage('');

    triggerRunMonthlyInvoices(undefined, {
      onSuccess: (data) => {
        const message =
          (data as { message?: string })?.message || 'Monthly invoices were triggered successfully.';
        setInvoiceRunStatus('success');
        setInvoiceRunMessage(message);
      },
      onError: (err) => {
        setInvoiceRunStatus('error');
        setInvoiceRunMessage(err instanceof Error ? err.message : 'Failed to run monthly invoices.');
      },
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" fontWeight="bold">
          Resellers
        </Typography>

        <Button variant="contained" onClick={handleRunMonthlyInvoices} disabled={isRunningMonthlyInvoices}>
          {isRunningMonthlyInvoices ? <CircularProgress size={18} /> : 'Run Monthly Invoices'}
        </Button>
      </Stack>

      {invoiceRunStatus !== 'idle' && (
        <Typography
          sx={{ mb: 2 }}
          color={invoiceRunStatus === 'success' ? 'success.main' : 'error.main'}
          fontSize={14}
        >
          {invoiceRunMessage}
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Debt Limit (£)</TableCell>
              <TableCell>Used (£)</TableCell>
              <TableCell>Available (£)</TableCell>
              <TableCell>Total Spend (£)</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row: any) => {
              const debtLimit = editedLimits[row.userId] ?? row.debtLimit ?? 0;
              const amountUsed = row.amountUsed ?? 0;
              const available = debtLimit - amountUsed;

              const isEditing = editingRows[row.userId];
              const isSaved = savedRows[row.userId];

              const hasChanged =
                editedLimits[row.userId] !== undefined && editedLimits[row.userId] !== row.debtLimit;

              return (
                <React.Fragment key={row.userId}>
                  <TableRow>
                    <TableCell>{row.userId?.slice(-6)}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={debtLimit}
                          onChange={(e) =>
                            setEditedLimits((prev) => ({
                              ...prev,
                              [row.userId]: Number(e.target.value),
                            }))
                          }
                        />
                      ) : (
                        <>£{row.debtLimit ?? 0}</>
                      )}
                    </TableCell>

                    <TableCell>£{Number(amountUsed).toFixed(2)}</TableCell>
                    <TableCell>£{Number(available).toFixed(2)}</TableCell>
                    <TableCell>£{Number(row.totalSpend ?? 0).toFixed(2)}</TableCell>

                    <TableCell>
                      {isEditing ? (
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSave(row)}
                            disabled={saving || !hasChanged}
                          >
                            {saving ? <CircularProgress size={18} /> : 'Save'}
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => handleCancel(row.userId)}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setEditingRows((prev) => ({
                              ...prev,
                              [row.userId]: true,
                            }))
                          }
                        >
                          Edit
                        </Button>
                      )}
                    </TableCell>
                    <TableCell width={48}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/reseller/admin-panel/${row.userId}`)}
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {isSaved && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="success.main" fontSize={14}>
                          ✅ Saved successfully
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AdminPanel;
